export type BuildInfo = {
  service: string;
  version: string;
  gitSha: string;
  shortSha: string;
  branch: string;
  builtAt: string;
  environment: string;
};

export const buildInfoPath = '/build-info.json';

export type BuildInfoFetchState = { status: 'success'; data: BuildInfo } | { status: 'error'; errorMessage: string };

export type DeployDiagnostics = {
  apiUrl: string;
  web: BuildInfoFetchState;
  api: BuildInfoFetchState;
};

const buildNoStoreUrl = (path: string) => {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}t=${Date.now()}`;
};

const readBuildInfoResponse = async (response: Response, label: string): Promise<BuildInfo> => {
  if (!response.ok) {
    throw new Error(`Failed to fetch ${label} build info: ${response.status}`);
  }

  return (await response.json()) as BuildInfo;
};

const toFetchState = (result: PromiseSettledResult<BuildInfo>): BuildInfoFetchState => {
  if (result.status === 'fulfilled') {
    return { status: 'success', data: result.value };
  }

  return {
    status: 'error',
    errorMessage: result.reason instanceof Error ? result.reason.message : String(result.reason),
  };
};

export const fetchWebBuildInfo = async (): Promise<BuildInfo> => {
  const response = await fetch(buildNoStoreUrl(buildInfoPath), { cache: 'no-store' });

  return readBuildInfoResponse(response, 'web');
};

export const fetchApiBuildInfo = async (apiUrl: string): Promise<BuildInfo> => {
  const { fetchWithTimeout } = await import('./api/common');
  const response = await fetchWithTimeout(buildNoStoreUrl(`${apiUrl}/api/version`), { cache: 'no-store' });

  return readBuildInfoResponse(response, 'API');
};

export const fetchDeployDiagnostics = async (): Promise<DeployDiagnostics> => {
  const { getApiUrl } = await import('./api/common');
  const apiUrl = getApiUrl();
  const [web, api] = await Promise.allSettled([fetchWebBuildInfo(), fetchApiBuildInfo(apiUrl)]);

  return {
    apiUrl,
    web: toFetchState(web),
    api: toFetchState(api),
  };
};

export const getBuildInfoValue = (state: BuildInfoFetchState, key: keyof BuildInfo): string => {
  if (state.status === 'success') {
    return state.data[key] || 'unknown';
  }

  return `unavailable (${state.errorMessage})`;
};

export const buildDeployDiagnosticsClipboardText = (diagnostics: DeployDiagnostics): string =>
  [
    'AgentTeams deployment diagnostics',
    `Web SHA: ${getBuildInfoValue(diagnostics.web, 'gitSha')}`,
    `Web short SHA: ${getBuildInfoValue(diagnostics.web, 'shortSha')}`,
    `Web built at: ${getBuildInfoValue(diagnostics.web, 'builtAt')}`,
    `Web version: ${getBuildInfoValue(diagnostics.web, 'version')}`,
    `API URL: ${diagnostics.apiUrl}`,
    `API SHA: ${getBuildInfoValue(diagnostics.api, 'gitSha')}`,
    `API short SHA: ${getBuildInfoValue(diagnostics.api, 'shortSha')}`,
    `API built at: ${getBuildInfoValue(diagnostics.api, 'builtAt')}`,
    `API version: ${getBuildInfoValue(diagnostics.api, 'version')}`,
  ].join('\n');
